import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Megaphone, Tag, Star, Gift, Bell, Link2, MapPin, Bot, Send, Plus, Loader2, Users,
  ExternalLink, Trophy, Award, BarChart3, MessageSquare, TrendingUp, UserCheck, RotateCcw
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend } from "recharts";
import { API_URL } from "@/utils/apiConfig";
import {
  HeroCard, StatCard, SectionHeader, InsightCard
} from "@/components/marketing/common";
import { MARKETING_COLORS } from "@/config/marketingColors";
import { LoyaltyCustomersRanking } from "./LoyaltyCustomersRanking";

const LoyaltyTab = () => {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [config, setConfig] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newReward, setNewReward] = useState({ points_required: 0, reward: "", discount_type: "percent", discount_value: 0 });
  const [googleLink, setGoogleLink] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      authAxiosRef.current.get("/loyalty/config").then(r => { setConfig(r.data); setGoogleLink(r.data?.google_review_link || ""); }),
      authAxiosRef.current.get("/loyalty/leaderboard").then(r => setCustomers(r.data)),
    ]).finally(() => setLoading(false));
  }, []);

  const save = async (data) => {
    try { await authAxios.patch("/loyalty/config", data); toast.success("Guardado!"); } catch { toast.error("Error"); }
  };

  const addReward = async () => {
    if (!newReward.reward || !newReward.points_required) return;
    const rewards = [...(config?.rewards || []), { id: Date.now().toString(), ...newReward, active: true }];
    await save({ rewards });
    setConfig(p => ({ ...p, rewards }));
    setNewReward({ points_required: 0, reward: "", discount_type: "percent", discount_value: 0 });
  };

  const handlePointsReset = (phone) => {
    setCustomers(prev => prev.map(c => c.phone === phone ? { ...c, points: 0 } : c));
  };

  const removeReward = (id) => {
    const rewards = (config?.rewards || []).filter(r => r.id !== id);
    save({ rewards });
    setConfig(p => ({ ...p, rewards }));
  };

  if (loading) return <LoadingScreen />;

  const totalCustomersEnrolled = customers.length;
  const totalPointsIssued = customers.reduce((sum, c) => sum + (c.points || 0), 0);

  return (
    <div className="space-y-6">
      {/* Hero Section */}
      <HeroCard
        title="Programa de Fidelização"
        metric={totalCustomersEnrolled}
        subMetrics={[
          {
            label: "Status",
            value: config?.enabled ? "Ativo" : "Inativo",
          },
          {
            label: "Total de Pontos Emitidos",
            value: totalPointsIssued.toLocaleString(),
          },
        ]}
        color={MARKETING_COLORS.Fidelidad}
      />

      {/* Status & Toggle */}
      <div className="flex items-center justify-between p-4 bg-card border border-border rounded-lg">
        <div>
          <p className="text-sm font-medium">Programa de Fidelização Ativo</p>
          <p className="text-xs text-muted-foreground mt-1">Os clientes acumulam pontos em cada compra</p>
        </div>
        <Switch checked={config?.enabled ?? true} onCheckedChange={v => { save({ enabled: v }); setConfig(p => ({ ...p, enabled: v })); }} data-testid="loyalty-toggle" />
      </div>

      {/* Configuration Section */}
      <SectionHeader
        title="Configuração"
        subtitle="Customize as regras do seu programa de fidelização"
      />

      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3 bg-slate-50 border-b">
            <CardTitle className="text-sm uppercase flex items-center gap-2">
              <span className="bg-amber-100 text-amber-600 p-2 rounded-md">
                <Gift className="w-4 h-4" />
              </span>
              Taxas
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="points-per-euro">Pontos por €100 gastos</Label>
              <Input
                id="points-per-euro"
                type="number"
                value={10}
                readOnly
                className="w-24"
                data-testid="points-per-euro"
              />
              <p className="text-xs text-muted-foreground">Quantos pontos o cliente ganha por cada euro gasto</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3 bg-slate-50 border-b">
            <CardTitle className="text-sm uppercase flex items-center gap-2">
              <span className="bg-blue-100 text-blue-600 p-2 rounded-md">
                <Star className="w-4 h-4" />
              </span>
              Google Reviews
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="google-review-link">Link de Avaliação</Label>
              <Input
                id="google-review-link"
                value={googleLink}
                onChange={e => setGoogleLink(e.target.value)}
                placeholder="https://g.page/r/..."
                data-testid="google-review-link"
              />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <p className="text-sm font-medium">Pedir Avaliação na Entrega</p>
                <p className="text-xs text-muted-foreground">Enviar WhatsApp pedindo avaliação em Google</p>
              </div>
              <Switch
                checked={config?.auto_send_review_request ?? true}
                onCheckedChange={v => { save({ auto_send_review_request: v }); setConfig(p => ({ ...p, auto_send_review_request: v })); }}
              />
            </div>
            <Button size="sm" onClick={() => save({ google_review_link: googleLink })} className="w-full">
              Salvar Link
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Rewards Section */}
      <SectionHeader
        title="Recompensas"
        subtitle="Configure as recompensas para os seus clientes mais fiéis"
      />

      <Card>
        <CardHeader className="pb-3 bg-slate-50 border-b">
          <CardTitle className="text-sm uppercase flex items-center gap-2">
            <span className="bg-amber-100 text-amber-600 p-2 rounded-md">
              <Trophy className="w-4 h-4" />
            </span>
            Recompensas Configuradas
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          {(config?.rewards || []).length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Nenhuma recompensa configurada. Adicione uma abaixo!</p>
          ) : (
            <div className="space-y-2">
              {(config?.rewards || []).map(r => (
                <div key={r.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-border">
                  <div className="flex items-center gap-3 flex-1">
                    <Trophy className="w-5 h-5 text-amber-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-sm">{r.reward}</p>
                      <p className="text-xs text-muted-foreground">{r.points_required} pontos • {r.discount_type === 'percent' ? `${r.discount_value}%` : r.discount_type === 'free_delivery' ? 'Frete Grátis' : `€${r.discount_value}`}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-700 h-8 w-8 p-0 shrink-0"
                    onClick={() => removeReward(r.id)}
                  >
                    ✕
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="pt-4 mt-4 border-t space-y-3">
            <Label className="text-sm font-medium">Adicionar Nova Recompensa</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Pontos (ex: 100)"
                value={newReward.points_required || ""}
                onChange={e => setNewReward(p => ({ ...p, points_required: parseInt(e.target.value) || 0 }))}
                className="w-32"
              />
              <Input
                placeholder="Descrição da recompensa"
                value={newReward.reward}
                onChange={e => setNewReward(p => ({ ...p, reward: e.target.value }))}
                className="flex-1"
                data-testid="new-reward-desc"
              />
            </div>
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs text-muted-foreground">Tipo de Desconto</Label>
                <Select value={newReward.discount_type} onValueChange={v => setNewReward(p => ({ ...p, discount_type: v }))}>
                  <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentagem (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (€)</SelectItem>
                    <SelectItem value="free_delivery">Frete Grátis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newReward.discount_type !== "free_delivery" && (
                <Input
                  type="number"
                  placeholder="0"
                  value={newReward.discount_value || ""}
                  onChange={e => setNewReward(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))}
                  className="w-24"
                />
              )}
              <Button onClick={addReward} size="sm" className="h-9" data-testid="add-reward-btn">
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Top Customers / Leaderboard Section */}
      <LoyaltyCustomersRanking
        customers={customers}
        onPointsReset={handlePointsReset}
      />

      {/* Program Benefits Insights */}
      <SectionHeader
        title="Benefícios do Programa"
        subtitle="Por que oferecer fidelização?"
      />

      <div className="grid md:grid-cols-3 gap-4">
        <InsightCard
          type="insight"
          title="Retenção de Clientes"
          message="Programas de pontos aumentam a frequência de compra em até 30% e reduzem churn de clientes."
        />
        <InsightCard
          type="success"
          title="Valor de Vida do Cliente"
          message="Clientes fiéis têm 3x mais valor de vida útil e propagam a marca por boca-a-boca."
        />
        <InsightCard
          type="alert"
          title="Vantagem Competitiva"
          message="Diferencie-se dos concorrentes com um programa personalizado que valoriza lealdade."
        />
      </div>
    </div>
  );
};

export { LoyaltyTab };
